import { useState } from 'react'
import { useAppDispatch, useAppState } from 'Context/AppContext/AppContext'
import {
  AiStoryAudienceType,
  AiStoryGoalType,
  useGetAiOptimizedStoryLazyQuery,
} from 'Generated/graphql-types'
import { useHistory } from 'react-router'
import { SET_AI_STORY_CONTENT } from 'TypeDefs/AppContext'
import { stories } from 'Modules/Analytics/trackActionInterface/Impact/Stories'
import {
  StoryVariants,
  AiStoryInput,
  AiGoalsStoryInput,
  StoryInput,
} from './StoryAiTypes'

const useAiStorySubmission = () => {
  const [hasError, setHasError] = useState(false)
  const [completed, setCompleted] = useState(false)
  const dispatch = useAppDispatch()
  const history = useHistory()
  const {
    nonprofitOrgConfig: { orgId, platformPlan, role, nonprofitOrgRole },
    funderOrgConfig: { orgId: funderOrgId },
    userConfig: { id, email },
  } = useAppState()

  const onCompleted = (data: any) => {
    const {
      blocks,
      template,
      hasError: genError,
    } = data.getAiOptimizedStory || {}
    if (!blocks || !template || genError) {
      setHasError(true)
      return
    }
    setCompleted(true)
    dispatch({
      type: SET_AI_STORY_CONTENT,
      payload: {
        blocks,
        template,
      },
    })
    // Dispatch or do whatever you need with blocks and template
    setTimeout(() => {
      history.push(`/impact/stories/new/AI_GENERATED`)
    }, 1000)
  }

  const [getOptimizedStory, { loading }] = useGetAiOptimizedStoryLazyQuery({
    onError: () => {
      setHasError(true)
    },
    onCompleted,
    fetchPolicy: 'network-only',
  })

  /*
   * @param {StoryInput} - The input data for the story
   * @returns {void} - No return value
   * @description - Handles the submission of the story input data
   * using storyVariant to determine the type of story to create
   */
  const handleSubmit = ({ aiInput, storyVariant }: StoryInput) => {
    setHasError(false)
    let goalId = null

    if (storyVariant === StoryVariants.GOALS && 'goal' in aiInput) {
      goalId = aiInput.goal
    }
    stories.Ai_Create_Story_Clicked({
      userId: id,
      nonprofitOrgId: orgId,
      goalId,
      platformPlan,
      funderOrgId,
      email,
      role,
      relationship: nonprofitOrgRole || '',
      storyVariant,
      dateCreated: new Date().toISOString(),
    })

    switch (storyVariant) {
      case StoryVariants.STORY:
        createAiStory(aiInput as AiStoryInput)
        break
      case StoryVariants.GOALS:
        createAiGoalsStory(aiInput as AiGoalsStoryInput)
        break
      default:
        break
    }
  }

  const createAiStory = (aiInput: AiStoryInput) => {
    getOptimizedStory({
      variables: {
        prompt: {
          additionalRequests: aiInput.additionalRequests,
          impact: aiInput.impact,
          importance: aiInput.importance,
          purpose: aiInput.purpose as AiStoryGoalType,
          audience: aiInput.audience as AiStoryAudienceType,
        },
        orgId,
      },
    })
  }

  const createAiGoalsStory = (aiInput: AiGoalsStoryInput) => {
    const prompt = {
      additionalRequests: aiInput.additionalRequests,
      purpose: aiInput.purpose as AiStoryGoalType,
      audience: aiInput.audience as AiStoryAudienceType,
    }
    getOptimizedStory({
      variables: {
        prompt,
        orgId,
        goalId: aiInput.goal,
      },
    })
  }

  // will need to change loading to incorporate loading from future queries
  return { handleSubmit, loading, hasError, completed }
}

export default useAiStorySubmission
